import React, { Component, Fragment } from 'react';
import { withRouter } from 'react-router';
import { Link } from 'react-router-dom';
import ReactLoading from 'react-loading';
import axios from 'axios';
import { get, uniqBy } from 'lodash-es';
import { Button, Col, FormGroup, Input, Label, Row } from 'reactstrap';

import InlineAlert from '../../../utilities/form-components/InlineAlert';
import {
  errorLoadingCatalogText,
  fileDeleteErrorText,
  offlineErrorText
} from '../../../utilities/StaticText';
import getLotExtension from './getLotExtension';
import imageUploadErrors from './imageUploadErrors';
import Step4Dropzone from './Step4Dropzone';
import Step4FileContainer from './Step4FileContainer';
import parseFileName from './parseFileName';
import { hostServer } from '../../../utilities/UrlPaths';

/**
 * This represents the parent component of Step4.  We need to maintain track of
 * files and their status in this component and pass it down to the sub-components.
 *
 * The first sub component is Step4Dropzone which represents the dropzone handler.
 * Upon file drop and file rejection - we handle the state here so that we can
 * accurately reflect errors and upload progress in other related components.
 *
 * The second sub-component is Step4FileContainer which represents the progress bar,
 * error handling, and contains the list of files.  Each file is a Step4File object.
 * When the user drags a file - it is immediately placed into the upload queue.
 *
 * Again - we track all file related state here and pass it down to the sub-components.
 */
class Step4 extends Component {
  constructor(props) {
    super(props);
    this.state = {
      error: false,
      fileDeleteError: false,
      files: [],
      fileUploadFormat: 'lotNumber',
      filterOnlyErrors: false,
      loading: true,
      lots: null,
      numFilesTotal: 0,
      offlineError: false
    };

    this.isUpdatedFileHandler = this.isUpdatedFileHandler.bind(this);
  }

  async componentDidMount() {
    const {
      match: {
        params: { catalogID }
      }
    } = this.props;

    const lotData = await axios(`/cps/app/catalog/${catalogID}/lots`);
    const allLots = get(lotData, 'data.data.lots', []);

    // Remove lots which are marked as 'deleted'
    const lots = allLots.filter(lot => lot.status !== 'd');

    if (lots.length > 0) {
      /**
       * This may seem extraneous to do two arrays/sets - but I think the performance boost on the
       * lotNumber case being able to use sets will be worth it.
       */
      const inventoryNumbers = lots.map(lot => ({
        invNumber: lot.invNumber,
        lotNumber: lot.lotNumber,
        lotID: lot.lotID,
        lotExt: lot.lotExt.toUpperCase()
      }));
      // Using sets here should be faster for an index lookup, and the numbers are ordered already
      // The unique value is the lot number + the lot extension
      const lotNumbers = new Set(lots.map(lot => lot.lotNumber + lot.lotExt.toUpperCase()));
      this.setState({ inventoryNumbers, lotNumbers, loading: false });
    } else {
      this.setState({ loading: false, error: true });
    }
  }

  abortFile = index => {
    const files = [...this.state.files];
    files.splice(index, 1);
    // If there are no more files with errors - deselect 'view items with errors'
    const { filterOnlyErrors, numFilesTotal } = this.state;
    if (filterOnlyErrors) {
      const errorsStillExist = files.some(file => file.hasError);
      this.setState({
        files,
        filterOnlyErrors: errorsStillExist,
        numFilesTotal: numFilesTotal - 1
      });
    } else {
      this.setState({ files, numFilesTotal: numFilesTotal - 1 });
    }
  };

  // We need to track errors here so we can display them above the list of files
  addFileError = (code, index) => {
    const {
      match: {
        params: { type }
      }
    } = this.props;

    const files = [...this.state.files];

    if (files[index]) {
      files[index].isComplete = true;
      files[index].hasError = true;
      files[index].error = imageUploadErrors(code, type);
      this.setState({ files });
    }
  };

  checkDuplicateFile = file => {
    const existingFiles = [...this.state.files];

    // Check if the file already exists
    const fileIndex = existingFiles.findIndex(
      existingFile => parseFileName(existingFile.name) === parseFileName(file.name)
    );
    if (fileIndex >= 0) {
      existingFiles.splice(fileIndex, 1);
      this.setState({ files: existingFiles });
    }
  };

  deleteFilesWithErrors = () => {
    const filesWithoutErrors = this.state.files.filter(file => !file.hasError);
    this.setState({
      files: filesWithoutErrors,
      filterOnlyErrors: false,
      numFilesTotal: filesWithoutErrors.length
    });
  };

  // DY May re-add this when the BE can do it efficiently - simpler implementation above
  /*
  deleteSelectedFiles = async () => {
    const files = [...this.state.files];

    // No files are selected, do nothing
    const selectedFiles = files.filter(file => file.isSelected);
    if (selectedFiles.length <= 0) {
      return;
    }

    const remainingFiles = files.filter(file => !file.isSelected);
  */

  /**
   * For each of the selected files - build our post data to send to the backend.
   * We only need to call the API for files without errors, since errored files
   * never actually uploaded
   */
  /*
    const filesToDelete = selectedFiles.filter(file => !file.hasError);
    if (filesToDelete.length === 0) {
      // Only deleting files with errors - no need to continue - be sure to clear checkbox
      this.setState({ files: remainingFiles, filterOnlyErrors: false });
      return;
    }

    const {
      match: {
        params: { catalogID }
      }
    } = this.props;
    const postDataArray = [];
    filesToDelete.forEach(file => postDataArray.push(file.photoID));
    try {
      await axios.delete(`/cps/app/images/catalog/${catalogID}`, { data: postDataArray });
      this.setState({ files: remainingFiles });
    } catch {
      this.setState({ fileDeleteError: true });
    }
  };
  */

  setStateSynchronous(stateUpdate) {
    return new Promise(resolve => {
      this.setState(stateUpdate, () => resolve());
    });
  }

  updateFileByIndex = async (updatedFile, index) => {
    const {
      match: {
        params: { catalogID }
      }
    } = this.props;

    try {
      const files = [...this.state.files];
      const file = files[index];

      files[index].isComplete = false;
      await this.setStateSynchronous({ ...this.state, files: [...files] });

      // The file was never uploaded so we only need to do the client side deletion
      if (file.hasError) {
        this.abortFile(index);
        return;
      }

      const { lotID, sequence } = this.parseFileByFileName(file.name);

      const postData = {
        catalogID,
        lotID,
        sequence,
        imgType: 'lot'
      };

      await axios.post('/cps/app/image/delete', postData, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      });

      updatedFile.hasError = false;
      updatedFile.error = null;
      updatedFile.isUpdatedFile = true;
      files[index] = updatedFile;
      await this.setStateSynchronous({ ...this.state, files: [...files] });
    } catch (e) {
      console.error(e);
    }
  };

  isUpdatedFileHandler(index) {
    const files = [...this.state.files];
    files[index].isUpdatedFile = false;
    this.setState({ files });
  }

  handleDeleteFile = async index => {
    const {
      match: {
        params: { catalogID }
      }
    } = this.props;

    try {
      const files = [...this.state.files];
      const file = files[index];

      // The file was never uploaded so we only need to do the client side deletion
      if (file.hasError) {
        this.abortFile(index);
        return;
      }

      const { lotID, sequence } = this.parseFileByFileName(file.name);

      const postData = {
        catalogID,
        lotID,
        sequence,
        imgType: 'lot'
      };

      await axios.post('/cps/app/image/delete', postData, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      });

      // Delete file from client state
      files.splice(index, 1);
      await this.setStateSynchronous({ files, numFilesTotal: this.state.numFilesTotal - 1 });
    } catch {
      this.setState({ fileDeleteError: true });
    }
  };

  handleErrorFilter = () => {
    const files = [...this.state.files];
    const { filterOnlyErrors } = this.state;
    if (filterOnlyErrors) {
      // Deselect all files that don't have errors
      files.filter(file => !file.hasError).forEach(file => (file.isSelected = false));
    }

    this.setState({ files, filterOnlyErrors: !filterOnlyErrors });
  };

  handleFileComplete = (index, photoID) => {
    const files = [...this.state.files];

    if (files[index]) {
      files[index].isComplete = true;
      files[index].photoID = photoID;
      this.setState({ files });
    }
  };

  /**
   * Triggers when a user drops or selects files on the dropzone.
   *
   * In step4 we need to handle this differently since the visual UX
   * is much different.
   */
  handleFileDrop = async files => {
    /**
     * Verify the user is connected to the internet before uploading files to
     * mitigate connection and timeout issues
     */
    if (!navigator.onLine) {
      this.setState({ offlineError: true });
      return;
    }

    /**
     * This resolves the event where the user drag/drops multiple files which all
     * have the same name.
     *
     * i.e. 7000.1.jpg , 7000_1.jpg , 7000-1.jpg should all be treated as the same file.
     */
    const uniqueFiles = uniqBy(files, file => parseFileName(file.name));

    // ESLint thinks file is undefined - but for loop is preferred here to forEach
    // eslint-disable-next-line
    for (const file of uniqueFiles) {
      // eslint-disable-next-line
      await this.checkDuplicateFile(file);

      file.error = {};
      file.hasError = false;
      file.isComplete = false;
      file.isSelected = false;
      file.photoID = null;
    }

    const numFilesTotal = this.state.files.length + files.length;
    this.setState({ numFilesTotal });

    /**
     * This gives the DOM time to catch up / process images when the user dumps 1000+
     * images all at the same time.
     */
    let fileCopy = [...files];
    const batchSize = 20;
    while (fileCopy.length > 0) {
      /**
       * The goal of this batching is to reduce the number of currently uploading
       * thumbnails.  Ensuring that the process is throttled will result in a
       * better experience for users on slower internet connections, without
       * reducing the optimum experience for those with faster internet connections.
       *
       * The more files that are stuck in the uploading phase - the more
       * browser memory is used in order to store thumbnails.  This batching will
       * check to make sure the number of files currently in the download process
       * is 100 or less before attempting to grab more files.
       *
       * When the uploads are complete - the memory is freed up
       * since the image points to the AWS URL.
       */
      const existingFiles = [...this.state.files];
      const remainingFiles = existingFiles.filter(file => !file.isComplete).length;

      if (remainingFiles < 100) {
        const nextFileBatch = fileCopy.slice(0, batchSize);
        fileCopy = fileCopy.splice(batchSize, fileCopy.length - 1);
        this.setState({
          files: [...existingFiles, ...nextFileBatch],
          offlineError: false
        });
      }

      // eslint-disable-next-line
      await new Promise(resolve => setTimeout(resolve, 3000));
    }
  };

  handleFileDropRejected = async files => {
    const {
      match: {
        params: { type }
      }
    } = this.props;

    /**
     * This resolves the event where the user drag/drops multiple files which
     * all have the same name.
     *
     * i.e. 7000.1.jpg , 7000_1.jpg , 7000-1.jpg should all be treated as the same file.
     */
    const uniqueFiles = uniqBy(files, file => parseFileName(file.name));

    // ESLint thinks file is undefined - but for loop is preferred here to forEach
    // eslint-disable-next-line
    for (const file of uniqueFiles) {
      // eslint-disable-next-line
      await this.checkDuplicateFile(file);

      /**
       * Cant use addFileError here because that requires the index and this
       * array will have conflicting indices prior to being mushed together in setState
       */
      file.error = imageUploadErrors('type', type);
      file.isComplete = true;
      file.isSelected = false;
      file.hasError = true;
    }

    const existingFiles = [...this.state.files];
    this.setState({ files: [...existingFiles, ...files] });
  };

  handleRadioChange = e => {
    this.setState({
      fileUploadFormat: e.target.id
    });
  };

  // DY may re-enable in the future
  /*
  handleSelectFile = index => {
    const files = [...this.state.files];
    files[index].isSelected = !files[index].isSelected;
    this.setState({ files });
  };
  */

  // DY may re-enable in the future
  /*
  handleSelectAll = () => {
    const { filterOnlyErrors } = this.state;
    const files = [...this.state.files];

    const selectedFiles = filterOnlyErrors ? files.filter(file => file.hasError) : files;
    const areAllSelected = selectedFiles.every(file => file.isSelected);

    // If all files aren't selected - select them all.  Otherwise de-select them all
    selectedFiles.forEach(file => (file.isSelected = !areAllSelected));
    this.setState({ files });
  };
  */

  /**
   * Helper function which takes a file name, and does some lookup logic to return the lotNumber,
   * lotExt, and sequence number.
   *
   * This is used by the deleteFile and deleteSelectedFiles functions
   */
  parseFileByFileName = fileName => {
    const { fileUploadFormat } = this.state;

    const splitFile = fileName.split('.');
    /**
     * This may or may not have the lot extension also so we have to parse
     * it if the upload is by lotNumber and not inventoryNumber
     */
    const lotOrInventoryNumber = parseFileName(splitFile[0]);
    const sequence = splitFile.length >= 3 ? splitFile[1] : '0';

    let lotNumber = '';
    let lotExt = '';
    let lotID = 0;
    let lotIndex = 0;
    const { inventoryNumbers } = this.state;

    if (fileUploadFormat === 'lotNumber') {
      const parseLotName = getLotExtension(lotOrInventoryNumber);
      lotNumber = parseLotName.lotNumber;
      lotExt = parseLotName.lotExt;
      lotIndex = inventoryNumbers.findIndex(
        image => image.lotNumber === lotNumber && image.lotExt === lotExt
      );
      lotID = inventoryNumbers[lotIndex].lotID;
    } else if (fileUploadFormat === 'inventoryNumber') {
      // We have to find the lot number and lot extension based on the inventory number
      lotIndex = inventoryNumbers.findIndex(
        inventoryNumber => inventoryNumber.invNumber === lotOrInventoryNumber
      );
      lotNumber = inventoryNumbers[lotIndex].lotNumber;
      lotExt = inventoryNumbers[lotIndex].lotExt;
      lotID = inventoryNumbers[lotIndex].lotID;
    }

    return { lotID, lotExt, lotNumber, sequence };
  };

  saveStepFour = async () => {
    const {
      match: {
        params: { catalogID, type }
      }
    } = this.props;

    const { files } = this.state;
    if (files.length > 0) {
      // (bnikom) send step to BE for step tracking if the user has uploaded files
      const stepNumber = type.toLowerCase() === 'bin' ? 4 : 5;
      await axios.post(`/cps/app/diy-step-tracker/${catalogID}`, stepNumber, {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json'
        }
      });
    }

    // Back to Auction Calendar
    this.props.history.push('/my-auctions/current');
  };

  skipStepFour = async () => {
    const {
      match: {
        params: { catalogID, groupID, type }
      }
    } = this.props;

    const stepNumber = type.toLowerCase() === 'bin' ? 4 : 5;

    await axios.post(`/cps/app/diy-step-tracker/${catalogID}`, stepNumber, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json'
      }
    });

    // Navigate to step 5 (Step 4 for BIN)
    this.props.history.push(
      `/session/${type}/${parseInt(groupID, 10)}/${parseInt(catalogID, 10)}/${stepNumber}`
    );
  };

  render() {
    const { error, loading } = this.state;

    if (loading) {
      return <ReactLoading className="loading-spinner" type="spin" color="black" />;
    }

    if (error) {
      return <InlineAlert baseText={errorLoadingCatalogText} />;
    }

    const {
      location: { state },
      match: {
        params: { catalogID, groupID, type }
      }
    } = this.props;
    const {
      files,
      fileDeleteError,
      fileUploadFormat,
      filterOnlyErrors,
      inventoryNumbers,
      lotNumbers,
      numFilesTotal,
      offlineError
    } = this.state;

    const isBin = type.toLowerCase() === 'bin';
    const isLinkedFromSummary = get(state, 'linkedFrom', null);
    const isEditingPage = !isBin && isLinkedFromSummary === 'step6';
    const isEditingPageBin = isBin && isLinkedFromSummary === 'step5';
    const isEditingSession = isEditingPage || isEditingPageBin;

    const conventionText = fileUploadFormat === 'lotNumber' ? 'Item' : 'Inventory';
    const conventionNumber = fileUploadFormat === 'lotNumber' ? '1' : 'ABC123';

    const numFilesComplete = files.filter(file => file.isComplete).length;
    const isUploading = numFilesTotal > 0 && numFilesComplete < numFilesTotal;

    return (
      <div className="mt-4">
        <Row>
          {/* Start Left Column */}
          <Col xs={8}>
            <strong>Upload Images</strong>
            <p className="mb-2">
              1. Drag &amp; drop images from your computer in the uploader box below (or click the
              &quot;choose a file(s) from your computer&quot; link to find your file)
            </p>
            <p className="mb-2">2. Correct Errors if shown.</p>
            <p>
              Note: any images that contain errors will not be uploaded or saved after clicking
              &quot;Continue&quot;.
            </p>

            <strong>Select Your File Format</strong>
            <p>You can choose to upload images by:</p>
            <FormGroup check>
              <Label for="lotNumber" check>
                <Input
                  id="lotNumber"
                  type="radio"
                  name="imageFormat"
                  disabled={files.length > 0}
                  checked={fileUploadFormat === 'lotNumber'}
                  onChange={e => this.handleRadioChange(e)}
                />
                {`${isBin ? 'Item' : 'Lot'} Number`}
              </Label>
            </FormGroup>
            <FormGroup check className="my-2">
              <Label for="inventoryNumber" check>
                <Input
                  id="inventoryNumber"
                  type="radio"
                  name="imageFormat"
                  disabled={files.length > 0}
                  checked={fileUploadFormat === 'inventoryNumber'}
                  onChange={e => this.handleRadioChange(e)}
                />
                Inventory Number
              </Label>
            </FormGroup>

            {offlineError && (
              <div className="mt-3">
                <InlineAlert baseText={offlineErrorText} showPhones={false} />
              </div>
            )}

            <Step4Dropzone
              acceptedFileList=".jpg,.jpeg,.png"
              handleFileDrop={this.handleFileDrop}
              handleFileDropRejected={this.handleFileDropRejected}
            />
          </Col>

          {/* Start Right Column */}
          <Col xs={4}>
            <strong>Image Requirements</strong>
            <p>Images must end in .JPG, .JPEG &amp; .PNG (Do NOT use .GIF)</p>
            <strong>Naming convention for {conventionText.toLowerCase()} number match:</strong>
            <p className="mb-0">Primary Photo:</p>
            <p className="mb-0">{conventionText}.FileType</p>
            <p>ex: {conventionNumber}.1.jpeg</p>

            <p className="mb-0">Additional Photos for Item:</p>
            <p className="mb-0">{conventionText}.SequenceNumber.FileType</p>
            <p>ex: {conventionNumber}.1.jpeg</p>

            {fileUploadFormat === 'lotNumber' && (
              <Fragment>
                <p className="mb-0">Items with extensions:</p>
                <p className="mb-0">{conventionText}Extension.SequenceNumber.FileType</p>
                <p>ex: {conventionNumber}A.2.jpeg</p>
              </Fragment>
            )}

            <Row>
              <Col>
                <strong>Max. Size</strong>
              </Col>
            </Row>

            <Row>
              <Col>
                <p>up to 10MB</p>
              </Col>
            </Row>

            <p>
              Use our{' '}
              <a
                rel="noopener noreferrer"
                target="_blank"
                href={`${hostServer}/inv/help-center/resource/catalog/preparing-images/`}
              >
                upload tip sheet
              </a>{' '}
              for help with uploading images.
            </p>
          </Col>
        </Row>

        {fileDeleteError && (
          <div className="mt-3">
            <InlineAlert baseText={fileDeleteErrorText} />
          </div>
        )}

        <Step4FileContainer
          abortFile={this.abortFile}
          addFileError={this.addFileError}
          cancelAllUploads={this.cancelAllUploads}
          catalogID={catalogID}
          deleteFilesWithErrors={this.deleteFilesWithErrors}
          files={files}
          fileUploadFormat={fileUploadFormat}
          filterOnlyErrors={filterOnlyErrors}
          handleErrorFilter={this.handleErrorFilter}
          handleFileComplete={this.handleFileComplete}
          handleDeleteFile={this.handleDeleteFile}
          inventoryNumbers={inventoryNumbers}
          isUploading={isUploading}
          lotNumbers={lotNumbers}
          numFilesTotal={numFilesTotal}
          numFilesComplete={numFilesComplete}
          type={type}
          updateFileByIndex={this.updateFileByIndex}
          isUpdatedFileHandler={this.isUpdatedFileHandler}
        />

        <div className="mt-4">
          <Button
            className={isUploading ? 'cursor-not-allowed' : ''}
            role="link"
            color="primary"
            disabled={isUploading}
            onClick={() => this.skipStepFour()}
          >
            {numFilesTotal === 0 ? 'Skip & Continue' : 'Continue'}
          </Button>
          {!isUploading && !isEditingSession && (
            <Button className="ml-5" role="link" color="link" onClick={() => this.saveStepFour()}>
              Save &amp; Exit
            </Button>
          )}
          {!isUploading && isEditingSession && (
            <Link
              className="ml-5"
              aria-label="Cancel & Return to Summary"
              to={`/session/${type}/${parseInt(groupID, 10)}/${parseInt(catalogID, 10)}/${
                isBin ? '5' : '6'
              }`}
            >
              Cancel &amp; Return to Summary
            </Link>
          )}
        </div>
      </div>
    );
  }
}

export default withRouter(Step4);
